import { makeAutoObservable } from 'mobx'
import { DayjsFormats } from 'shared/lib'
import { uiStore } from 'shared/store/uiStore'
import {
  AdvancedSchedulingStore,
  type IInitAdvancedScheduling,
} from 'shared/ui/Schedule/ui/AdvancedScheduling'
import { getDefaultQuarterTime } from 'shared/ui/Schedule/utils/getDefaultQuarterTime'
import { type IInitSendingHours, SendingHoursStore } from 'shared/ui/Schedule/ui/SendingHours'

export type IInitSendLaterStoreProps = {
  date: string | null
  isUseContactTimezone: boolean
  advancedDate?: IInitAdvancedScheduling
  sendingHoursDate?: IInitSendingHours
}

export type ISendLaterStoreProps = {
  withAdvancedScheduling?: boolean
  withRestrictSendingHours?: boolean
}

export class SendLaterStore {
  private _advancedSchedulingStore: AdvancedSchedulingStore | null = null
  private _sendingHoursStore: SendingHoursStore | null = null

  constructor({ withAdvancedScheduling, withRestrictSendingHours }: ISendLaterStoreProps) {
    if (withAdvancedScheduling) {
      this._advancedSchedulingStore = new AdvancedSchedulingStore(
        this.handleAdvancedSchedulingCallBack
      )
    }

    if (withRestrictSendingHours) {
      this._sendingHoursStore = new SendingHoursStore(this.handleSendingHoursCallBack)
    }

    makeAutoObservable(this)
  }

  private _isUseContactTimezoneDisabled = false
  private _defaultDate = getDefaultQuarterTime()

  private _date: Date | null = this.defaultDate
  private _dropdownId = ''
  private _isUseContactTimezone = false

  handleAdvancedSchedulingCallBack = (status: boolean) => {
    if (this.isUseContactTimezone) return

    this.handleSendingHoursDisabled(status)
  }

  handleSendingHoursCallBack = (status: boolean) => {
    this.advancedSchedulingStore?.setAdvancedScheduleDisabled(status)
    this.setUseContactTimezoneDisabled(status)
  }

  get isUseContactTimezone() {
    return this._isUseContactTimezone
  }

  get defaultDate() {
    return this._defaultDate
  }

  get date() {
    return this._date
  }

  get dropdownId() {
    return this._dropdownId
  }

  get advancedSchedulingStore() {
    return this._advancedSchedulingStore
  }

  get sendingHoursStore() {
    return this._sendingHoursStore
  }

  get isSendingHoursEnabled() {
    return this.sendingHoursStore?.isEnabled
  }

  get laterDate() {
    return uiStore.dayjs(this.date || this.defaultDate).add(-uiStore.offsetMinute, 'minutes')
  }

  get utcDateString() {
    return this.laterDate.utc().format(DayjsFormats.apiFormat2)
  }

  get scheduleText() {
    return this.laterDate.format(DayjsFormats.fullWithAtDash3)
  }

  get isUseContactTimezoneDisabled() {
    return this._isUseContactTimezoneDisabled
  }

  init = ({
    date,
    isUseContactTimezone,
    advancedDate,
    sendingHoursDate,
  }: IInitSendLaterStoreProps) => {
    if (date) {
      this.setDate(uiStore.dayjs(date).add(uiStore.offsetMinute, 'minutes').toDate(), true)
    }
    this._isUseContactTimezone = isUseContactTimezone

    if (isUseContactTimezone) {
      this.handleSendingHoursDisabled(true)
    }

    if (this.advancedSchedulingStore && advancedDate) {
      this.advancedSchedulingStore.init(advancedDate)
      this.handleSendingHoursDisabled(advancedDate.isAdvancedScheduling || isUseContactTimezone)
    }

    if (sendingHoursDate) {
      this.sendingHoursStore?.init(sendingHoursDate)
      this.advancedSchedulingStore?.setAdvancedScheduleDisabled(sendingHoursDate.isSendingHours)
      this.setUseContactTimezoneDisabled(sendingHoursDate.isSendingHours)
    }
    if (sendingHoursDate) {
      this.sendingHoursStore?.init(sendingHoursDate)
    }
  }

  initDefault = () => {
    this._defaultDate = getDefaultQuarterTime()
  }

  toggleUseContactTimezone = () => {
    if (this.isUseContactTimezoneDisabled) return

    this._isUseContactTimezone = !this.isUseContactTimezone

    if (!this.advancedSchedulingStore?.isEnabled) {
      this.handleSendingHoursDisabled(this.isUseContactTimezone)
    }
  }

  setDropdownId = (dropdownId: string) => {
    this._dropdownId = dropdownId
    this.advancedSchedulingStore?.setDropdownId(dropdownId)
    this.sendingHoursStore?.setDropdownId(dropdownId)
  }

  setDate = (date: Date | null, isNoValidate?: boolean) => {
    this._defaultDate = getDefaultQuarterTime(date)
    this._date = date
    if (this.laterDate.isBefore(uiStore.dayjs()) && !isNoValidate) {
      this._defaultDate = getDefaultQuarterTime()
      this._date = this.defaultDate
    }

    this.advancedSchedulingStore?.setDate(this.date || this.defaultDate)
  }

  setUseContactTimezoneDisabled = (status: boolean) => {
    this._isUseContactTimezoneDisabled = status
  }

  handleSendingHoursDisabled = (status: boolean) => {
    this.sendingHoursStore?.setSendingHoursDisabled(status)
  }
}
